import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiShieldCheck, HiXMark } from 'react-icons/hi2';
import { requestClientLoginOtp, verifyClientLoginOtp } from '../../services/settingsService';
import toast from 'react-hot-toast';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
};

const OTP_LENGTH = 6;

export default function LoginOtpInputPrompt({ email, open, onOtpVerified, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [otpRequestId, setOtpRequestId] = useState(null);
  const [expiryMinutes, setExpiryMinutes] = useState(10);
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [showResend, setShowResend] = useState(false);
  const inputRefs = useRef([]);
  const hasRequestedOtp = useRef(false);

  useEffect(() => {
    if (!open || !email) {
      hasRequestedOtp.current = false;
      return;
    }

    if (hasRequestedOtp.current) return;

    let isMounted = true;

    const initOtp = async () => {
      try {
        setInitializing(true);
        hasRequestedOtp.current = true;
        setError('');

        // Normalize email before sending to match backend expectation
        const normalizedEmail = email?.toLowerCase().trim();
        if (!normalizedEmail) {
          throw new Error('Email is required');
        }

        const start = await requestClientLoginOtp(normalizedEmail);
        if (!isMounted) return;

        // If OTP is not enabled for this user, skip verification
        if (!start.clientOtpEnabled) {
          setInitializing(false);
          if (onOtpVerified) {
            await onOtpVerified({ skipped: true, reason: 'clientOtpDisabled' });
          }
          return;
        }

        if (!start.otpRequestId) {
          throw new Error('Failed to receive OTP request ID');
        }

        setOtpRequestId(start.otpRequestId);
        setExpiryMinutes(start.expiryMinutes || 10);
        setDigits(Array(OTP_LENGTH).fill(''));
        setError('');
        setShowResend(false);
        toast.success('OTP sent! Check your inbox.');
      } catch (e) {
        if (!isMounted) return;
        hasRequestedOtp.current = false;
        let errorMsg = 'Unable to send login OTP. Please try again.';

        if (e?.message) {
          errorMsg = e.message;
        }

        toast.error(errorMsg);
        setError(errorMsg);
      } finally {
        if (isMounted) setInitializing(false);
      }
    };

    initOtp();

    return () => {
      isMounted = false;
    };
  }, [open, email]);

  const handleVerify = async (e, codeOverride) => {
    e?.preventDefault?.();
    const code = codeOverride ?? digits.join('');
    if (!otpRequestId || code.length !== OTP_LENGTH) {
      setError('Please enter the 6-digit code sent to your email.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setShowResend(false);

      const normalizedEmail = email?.toLowerCase().trim();

      const res = await verifyClientLoginOtp({
        otpRequestId,
        email: normalizedEmail,
        otp: code,
      });

      if (res.success) {
        toast.success('OTP verified successfully. Completing login...');
        if (onOtpVerified) {
          onOtpVerified({ ...res, otp: code });
        }
      } else {
        setDigits(Array(OTP_LENGTH).fill(''));
        setError('Invalid OTP. Please try again or resend.');
        setShowResend(true);
        toast.error('Invalid OTP. Please try again or resend a new code.');
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      let errorMessage = 'Invalid or expired OTP. Please try again.';

      if (err?.code === 'deadline-exceeded' || err?.message?.includes('expired')) {
        errorMessage = 'OTP has expired. Please request a new code.';
      } else if (err?.code === 'invalid-argument' || err?.message?.includes('Invalid')) {
        errorMessage = 'Invalid or expired OTP. Please try again.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = err?.message || 'OTP has already been used or is invalid.';
      } else if (err?.code === 'resource-exhausted') {
        errorMessage = 'Too many incorrect attempts. Please request a new OTP.';
      } else if (err?.code === 'not-found') {
        errorMessage = 'OTP request not found. Please request a new code.';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setDigits(Array(OTP_LENGTH).fill(''));
      setError(errorMessage);
      setShowResend(true);
      toast.error(errorMessage);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (index, value) => {
    const char = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = char;
    setDigits(next);
    setError('');

    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (char && index === OTP_LENGTH - 1) {
      const fullCode = next.join('');
      if (fullCode.length === OTP_LENGTH) {
        setDigits(next);
        setTimeout(() => handleVerify(null, fullCode), 0);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
      setError('');
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);
    setError('');
    if (pasted.length === OTP_LENGTH) {
      setTimeout(() => handleVerify(null, pasted), 0);
    } else {
      inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      setLoading(true);
      setError('');
      hasRequestedOtp.current = false;

      // Normalize email before sending to match backend expectation
      const normalizedEmail = email?.toLowerCase().trim();
      if (!normalizedEmail) {
        throw new Error('Email is required');
      }

      const start = await requestClientLoginOtp(normalizedEmail);

      // If OTP is not enabled for this user, skip verification
      if (!start.clientOtpEnabled) {
        setLoading(false);
        if (onOtpVerified) {
          await onOtpVerified({ skipped: true, reason: 'clientOtpDisabled' });
        }
        return;
      }

      if (!start.otpRequestId) {
        throw new Error('Failed to receive OTP request ID');
      }

      hasRequestedOtp.current = true;
      setOtpRequestId(start.otpRequestId);
      setExpiryMinutes(start.expiryMinutes || 10);
      setDigits(Array(OTP_LENGTH).fill(''));
      setShowResend(false);
      toast.success('A new OTP has been sent to your email.');
    } catch (e) {
      hasRequestedOtp.current = false;
      let errorMsg = 'Failed to resend OTP. Please try again.';

      if (e?.message) {
        errorMsg = e.message;
      }

      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && onCancel) onCancel();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="login-otp-input-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          style={{ zIndex: 9999 }}
        >
          <motion.div
            key="login-otp-input-modal"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:w-full"
            style={{ zIndex: 10000 }}
          >
            <button
              type="button"
              onClick={handleClose}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <HiXMark className="h-5 w-5" />
            </button>

            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
                <HiShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Verify Your Login</h2>
                <p className="text-xs text-gray-600">
                  Enter the OTP sent to <span className="font-medium">{email}</span>
                </p>
              </div>
            </div>

            {initializing ? (
              <div className="flex flex-col items-center justify-center gap-5 py-10">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-900">Sending OTP to your email</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Please wait while we send your verification code to{' '}
                    <span className="font-medium text-gray-700">{email}</span>
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    One-Time Password
                  </label>
                  <div className="flex justify-center gap-2" onPaste={handlePaste}>
                    {digits.map((d, i) => (
                      <input
                        key={i}
                        ref={el => (inputRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={d}
                        onChange={e => handleDigitChange(i, e.target.value)}
                        onKeyDown={e => handleKeyDown(i, e)}
                        disabled={loading}
                        className={`h-12 w-12 rounded-lg border-2 text-center text-xl font-bold text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:opacity-50 sm:h-14 sm:w-14 sm:text-2xl ${
                          error ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-gray-50'
                        }`}
                        aria-label={`Digit ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>

                {error && (
                  <p className="text-center text-[13px] font-medium text-red-600">{error}</p>
                )}
                <p className="text-center text-xs text-gray-500">
                  Code expires in approximately {expiryMinutes} minutes.
                </p>

                <div className="flex flex-col gap-3 pt-2">
                  {showResend && (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={loading}
                      className="w-full rounded-lg border-2 border-blue-600 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  )}
                  <div
                    className={`flex gap-2 ${showResend ? 'justify-center' : 'justify-between'}`}
                  >
                    <button
                      type="button"
                      onClick={handleClose}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    {!showResend && (
                      <button
                        type="button"
                        onClick={() => setShowResend(true)}
                        disabled={loading}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      >
                        Resend code
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={loading || digits.join('').length !== OTP_LENGTH}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Verifying…' : 'Verify OTP'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// /Users/bakangmonei/Developer/Projects/411-group/appConfig.json
